// Saved-route data shapes + pure helpers. The fetch functions that use these stay
// per-app (coupled to each app's API config) — see apps/*/src/lib/savedRoutes.ts.

import { haversineKm } from './cameras';

/**
 * Per-segment routing mode. 'straight' means no routing — a direct line (M3 editor).
 * 'track' also never routes: it carries verbatim geometry (a GPX/ride line opened in the
 * editor); splitting one preserves the line exactly, and only re-routing a stretch (mode
 * change or endpoint drag) replaces its geometry.
 */
export type SegmentMode = 'fast' | 'scenic' | 'curvy' | 'straight' | 'track';

export type RouteGravel = 'exclude' | 'neutral' | 'include';

export type RouteSource = 'planner' | 'gpx' | 'ride' | 'editor';

export type RouteVisibility = 'private' | 'link' | 'public';

export interface PlanWaypoint {
  lat: number;
  lon: number;
  name?: string;
}

export interface PlanSegment {
  mode: SegmentMode;
  /** Ignored when mode is 'straight' or 'track' (neither calls the router). */
  gravel: RouteGravel;
  /** Index into the decoded FULL polyline where this segment ends (inclusive). */
  endIndex: number;
}

/**
 * The editing recipe stored alongside a route's geometry snapshot. Read only when
 * editing — display/share/export always use the stored polyline. A 'waypoints' plan
 * reopens the editor by slicing the polyline at the segment endIndex marks (zero routing
 * calls); a 'roundTrip' plan reopens the planner pre-filled instead (the editor can't
 * reproduce GraphHopper's round_trip algorithm).
 */
export type RoutePlan =
  | {
      version: 1;
      kind: 'waypoints';
      /** n waypoints, n−1 segments; endIndex strictly increasing, last = coords.length−1. */
      waypoints: PlanWaypoint[];
      segments: PlanSegment[];
    }
  | {
      version: 1;
      kind: 'roundTrip';
      start: PlanWaypoint;
      distanceKm: number;
      headingDegrees?: number;
      seed: number;
      mode: Exclude<SegmentMode, 'straight' | 'track'>;
      gravel: RouteGravel;
    };

/** Summary row from GET /me/routes — mirrors the backend RouteSummaryDto. No full geometry. */
export interface RouteSummary {
  id: string;
  name: string;
  description: string | null;
  source: RouteSource;
  visibility: RouteVisibility;
  distanceMeters: number;
  /** Estimated riding time; null when unknowable (GPX import). */
  durationSeconds: number | null;
  /** Google-encoded simplified geometry, for the card sparkline / initial map fit. */
  overviewPolyline: string;
  hasPlan: boolean;
  createdAt: string; // ISO 8601
  updatedAt: string;
  // M2 sharing fields — optional so M1-era mirror records (and the mobile app, which
  // hasn't ported M2) stay valid. Absent means: own live route.
  /** True for the user's own routes; false for followed ones. */
  owned?: boolean;
  /** True when the owner deleted/unshared this route — a followers-only snapshot. */
  isFrozen?: boolean;
  /** Owner display name for followed rows ("by {owner}"); null/absent for own routes. */
  ownerName?: string | null;
}

/** Full route from GET /routes/{id} — mirrors the backend RouteDetailDto. */
export interface RouteDetail {
  id: string;
  name: string;
  description: string | null;
  source: RouteSource;
  visibility: RouteVisibility;
  distanceMeters: number;
  durationSeconds: number | null;
  overviewPolyline: string;
  createdAt: string;
  updatedAt: string;
  /** Full geometry as a Google encoded polyline (precision 1e5). */
  polyline: string;
  /** Null for geometry-only routes (GPX import, ride conversion) — not editable. */
  plan: RoutePlan | null;
  /** Owner display name; null when the requester is the owner. */
  ownerName: string | null;
  // M2 sharing fields — optional for M1-era mirror records. Absent means: own live route.
  owned?: boolean;
  following?: boolean;
  isFrozen?: boolean;
}

/** Reduces a detail to its summary shape (what the library list renders). */
export function toRouteSummary(detail: RouteDetail): RouteSummary {
  return {
    id: detail.id,
    name: detail.name,
    description: detail.description,
    source: detail.source,
    visibility: detail.visibility,
    distanceMeters: detail.distanceMeters,
    durationSeconds: detail.durationSeconds,
    overviewPolyline: detail.overviewPolyline,
    hasPlan: detail.plan !== null,
    createdAt: detail.createdAt,
    updatedAt: detail.updatedAt,
    owned: detail.owned,
    isFrozen: detail.isFrozen,
    ownerName: detail.ownerName,
  };
}

/**
 * Merges server routes with locally-mirrored ones, keyed by id. The server copy wins
 * (it holds the normalised geometry); a not-yet-synced local save still shows. Sorted
 * newest-saved first — same ordering as GET /me/routes, so appended pages interleave
 * consistently.
 */
export function mergeRoutes(server: RouteSummary[], local: RouteSummary[]): RouteSummary[] {
  const byId = new Map<string, RouteSummary>();
  for (const r of local) byId.set(r.id, r);
  for (const r of server) byId.set(r.id, r); // server wins on conflict
  return [...byId.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/**
 * Builds the editing recipe for an imported line whose via points are known (GPX `<wpt>`
 * entries — route planners put them there, and MotoGo's own exports do too): each point
 * becomes a via splitting the track at its nearest polyline vertex, so the route opens
 * in the editor with its original waypoints instead of one start-to-finish track. Points
 * further than `maxSnapMeters` from the line are POIs (fuel stops, viewpoints), not vias,
 * and are skipped. Returns null when no point survives as an interior via — the plan-less
 * track hydration covers that route just as well.
 *
 * `coordinates` are [lon, lat] pairs (GeoJSON order). Waypoints in the returned plan
 * carry the snapped vertex position (so editor discs sit exactly on the line) but keep
 * the point's name.
 */
export function buildTrackPlan(
  coordinates: [number, number][],
  points: PlanWaypoint[],
  maxSnapMeters = 500,
): RoutePlan | null {
  if (coordinates.length < 3 || points.length === 0) return null;

  // endIndex must be strictly increasing over interior vertices: snap each point, drop
  // far-off POIs and endpoint hits, dedupe shared vertices, then order along the line.
  const snapped = new Map<number, PlanWaypoint>();
  for (const point of points) {
    let best = -1;
    let bestKm = Infinity;
    for (let i = 0; i < coordinates.length; i++) {
      const km = haversineKm(point.lat, point.lon, coordinates[i][1], coordinates[i][0]);
      if (km < bestKm) {
        bestKm = km;
        best = i;
      }
    }
    if (bestKm * 1000 > maxSnapMeters || best <= 0 || best >= coordinates.length - 1) continue;
    if (!snapped.has(best)) snapped.set(best, point);
  }
  if (snapped.size === 0) return null;

  // Server cap is 200 waypoints total — keep the first 198 vias plus start/finish.
  const viaIndexes = [...snapped.keys()].sort((a, b) => a - b).slice(0, 198);

  const at = (index: number, name?: string): PlanWaypoint => ({
    lat: coordinates[index][1],
    lon: coordinates[index][0],
    ...(name ? { name } : {}),
  });

  return {
    version: 1,
    kind: 'waypoints',
    waypoints: [at(0), ...viaIndexes.map((i) => at(i, snapped.get(i)!.name)), at(coordinates.length - 1)],
    segments: [...viaIndexes, coordinates.length - 1].map((endIndex) => ({
      mode: 'track' as const,
      gravel: 'neutral' as const,
      endIndex,
    })),
  };
}

/**
 * Slices a route's decoded geometry back into per-segment pieces using the plan's
 * endIndex marks — how the M3 editor reopens a saved route with zero routing calls.
 * Segment i spans indices [previousEnd .. endIndex] inclusive, so adjacent segments
 * share their joint coordinate. Returns null for non-waypoints plans (roundTrip reopens
 * the planner instead) and for plans that don't fit the geometry.
 */
export function slicePlanSegments<T>(
  coords: T[],
  plan: RoutePlan,
): { mode: SegmentMode; gravel: RouteGravel; coords: T[] }[] | null {
  if (plan.kind !== 'waypoints') return null;
  const last = plan.segments[plan.segments.length - 1];
  if (!last || last.endIndex !== coords.length - 1) return null;

  const slices: { mode: SegmentMode; gravel: RouteGravel; coords: T[] }[] = [];
  let start = 0;
  for (const segment of plan.segments) {
    if (segment.endIndex <= start - 1) return null; // malformed: not strictly increasing
    slices.push({
      mode: segment.mode,
      gravel: segment.gravel,
      coords: coords.slice(start, segment.endIndex + 1),
    });
    start = segment.endIndex;
  }
  return slices;
}
